<template>
    <div class="bill-calculation">
        <!-- 헤더 -->
        <div class="content-header">
            <h3>
                <i class="fas fa-calculator"></i>
                요금 계산
            </h3>
        </div>

        <!-- 2컬럼 레이아웃: 왼쪽(계약 목록) + 오른쪽(월별 결과) -->
        <div class="calculation-layout">
            <!-- 왼쪽 패널: 날짜 선택, 계산 실행, 계약 목록 -->
            <div class="left-panel">
                <!-- 날짜 선택 및 계산 실행 + 계약 목록 -->
                <div class="contract-list-section">
                    <div class="date-form-compact">
                        <div class="form-field-inline">
                            <label>시작일 <span class="required">*</span></label>
                            <div
                                class="date-picker-container-compact"
                                v-click-outside="closeFromDatePicker"
                                @click="toggleFromDatePicker"
                            >
                                <div class="date-display-compact">
                                    <span>{{ fromDateDisplay }}</span>
                                    <i :class="['fas fa-chevron-down', { rotate: showFromDatePicker }]"></i>
                                </div>
                                <!-- 드롭다운 -->
                                <div v-if="showFromDatePicker" class="date-dropdown date-dropdown-left">
                                    <div class="year-selector">
                                        <button @click.stop="changeFromYear(-1)">
                                            <i class="fas fa-chevron-left"></i>
                                        </button>
                                        <span>{{ fromDateYear }}</span>
                                        <button @click.stop="changeFromYear(1)">
                                            <i class="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    <div class="month-grid">
                                        <button
                                            v-for="month in 12"
                                            :key="month"
                                            :class="['month-btn', { active: month === fromDateMonth }]"
                                            @click.stop="selectFromMonth(month)"
                                        >
                                            {{ month }}월
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="form-field-inline">
                            <label>종료일 <span class="required">*</span></label>
                            <div
                                class="date-picker-container-compact"
                                v-click-outside="closeToDatePicker"
                                @click="toggleToDatePicker"
                            >
                                <div class="date-display-compact">
                                    <span>{{ toDateDisplay }}</span>
                                    <i :class="['fas fa-chevron-down', { rotate: showToDatePicker }]"></i>
                                </div>
                                <!-- 드롭다운 -->
                                <div v-if="showToDatePicker" class="date-dropdown date-dropdown-right">
                                    <div class="year-selector">
                                        <button @click.stop="changeToYear(-1)">
                                            <i class="fas fa-chevron-left"></i>
                                        </button>
                                        <span>{{ toDateYear }}</span>
                                        <button @click.stop="changeToYear(1)">
                                            <i class="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    <div class="month-grid">
                                        <button
                                            v-for="month in 12"
                                            :key="month"
                                            :class="['month-btn', { active: month === toDateMonth }]"
                                            @click.stop="selectToMonth(month)"
                                        >
                                            {{ month }}월
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="form-field-inline">
                            <button
                                class="btn-calculate-compact"
                                @click="executeCalculation"
                                :disabled="calculating || selectedContracts.length === 0"
                            >
                                <i :class="['fas', calculating ? 'fa-spinner fa-spin' : 'fa-play']"></i>
                                {{ calculating ? "계산 중..." : "계산 실행" }}
                            </button>
                        </div>
                    </div>
                    <h6 class="section-title-compact">계약 목록</h6>
                    <table class="contract-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        :checked="isAllContractsSelected"
                                        @change="toggleSelectAllContracts"
                                    />
                                </th>
                                <th>공급사</th>
                                <th>계약명</th>
                                <th>요금제명</th>
                                <th>계약 용량</th>
                                <th>관제점주소</th>
                                <th>계약 기간</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="contract in filteredContracts"
                                :key="contract.contractIdx"
                                :class="{ selected: selectedContractIdx === contract.contractIdx }"
                                @click="selectContract(contract.contractIdx)"
                            >
                                <td @click.stop>
                                    <input
                                        type="checkbox"
                                        :value="contract.contractIdx"
                                        v-model="selectedContracts"
                                        @click.stop
                                    />
                                </td>
                                <td>{{ contract.supplier?.supplierName || "-" }}</td>
                                <td>{{ contract.contractName || "-" }}</td>
                                <td>{{ contract.cost?.costName || "-" }}</td>
                                <td>{{ contract.contractCapacity || "-" }}</td>
                                <td>{{ contract.point?.ptAddr || "-" }}</td>
                                <td>
                                    <div class="period-info">
                                        <span>{{ formatDate(contract.startDate) }}</span>
                                        <span v-if="contract.endDate"> ~ {{ formatDate(contract.endDate) }}</span>
                                        <span v-else class="ongoing-badge">진행중</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- 오른쪽 패널: 선택된 계약의 월별 결과 -->
            <div class="right-panel">
                <div v-if="selectedContractIdx && calculationResults[selectedContractIdx]" class="result-panel">
                    <h6 class="section-title-compact">{{ getContractName(selectedContractIdx) }} - 월별 결과</h6>
                    <div class="month-results">
                        <div
                            v-for="(result, regYm) in getSelectedContractResults()"
                            :key="regYm"
                            class="month-result-card"
                        >
                            <div class="month-result-header">
                                <span class="month-label">
                                    {{
                                        `${formatYearMonth(regYm)} 
                                        (${formatDate(result.result?.billingStartAt) || "-"} ~ ${formatDate(result.result?.billingEndAt) || "-"})`
                                    }}
                                </span>
                                <span class="month-summary" v-if="result.success && result.result">
                                    {{ formatNumber(result.result.totalFee, 0) }}원
                                </span>
                                <span
                                    class="month-summary"
                                    v-else-if="!result.success"
                                    style="color: var(--ebc-danger)"
                                >
                                    계산 실패<br />
                                    <small>{{ result.error || "-" }}</small>
                                </span>
                                <button
                                    class="btn-detail-modal"
                                    @click="openDetailModal(selectedContractIdx, regYm)"
                                    v-if="result.success"
                                >
                                    <i class="fas fa-info-circle"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="placeholder-section">
                    <div class="placeholder-content">
                        <i class="fas fa-calculator"></i>
                        <p>계약을 선택하고<br />계산을 실행하면<br />월별 결과가 표시됩니다.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- 프로그래스 팝업 -->
        <b-modal
            modal-class="ebc-settings-dialog"
            ref="progressModal"
            :title="progress.completed >= progress.total ? '계산 완료' : '계산 진행 중'"
            :hide-footer="true"
            :no-close-on-backdrop="progress.completed < progress.total"
            :no-close-on-esc="progress.completed < progress.total"
            centered
        >
            <div class="progress-modal-content">
                <div class="progress-info">
                    <i v-if="progress.completed < progress.total" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-check-circle progress-complete-icon"></i>
                    <span>{{
                        progress.current || (progress.completed >= progress.total ? "계산 완료" : "계산 준비 중...")
                    }}</span>
                </div>
                <div class="progress-bar-wrapper">
                    <div class="progress-bar" :style="{ width: `${progressPercentage}%` }"></div>
                </div>
                <div class="progress-text">{{ progress.completed }} / {{ progress.total }} 완료</div>
                <div v-if="progress.completed < progress.total" class="progress-actions">
                    <button class="btn-cancel-calculation" @click="cancelCalculation">
                        <i class="fas fa-times"></i>
                        계산 취소
                    </button>
                </div>
                <div v-else class="progress-actions">
                    <button class="btn-close-progress" @click="closeProgressModal">
                        <i class="fas fa-check"></i>
                        확인
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- 결과 상세 모달 -->
        <b-modal
            modal-class="ebc-settings-dialog"
            ref="detailModal"
            title="계산 결과 상세"
            size="lg"
            :hide-footer="true"
        >
            <div v-if="detailModalData" class="detail-modal-content">
                <!-- 단일 컬럼 레이아웃 -->
                <div class="detail-modal-column">
                    <!-- 기본 정보 -->
                    <div class="info-section-compact">
                        <h6 class="section-title-compact">기본 정보</h6>
                        <div class="info-grid-compact">
                            <div class="info-row-compact">
                                <span class="info-label-compact">계약명</span>
                                <span class="info-value-compact">{{
                                    getContractName(detailModalData.contractIdx)
                                }}</span>
                            </div>
                            <div class="info-row-compact">
                                <span class="info-label-compact">집계년월</span>
                                <span class="info-value-compact">{{ formatYearMonth(detailModalData.regYm) }}</span>
                            </div>
                            <div class="info-row-compact">
                                <span class="info-label-compact">청구 기간</span>
                                <span class="info-value-compact">
                                    {{ formatDate(detailModalData.result.billingStartAt) }} ~
                                    {{ formatDate(detailModalData.result.billingEndAt) }}
                                </span>
                            </div>
                            <div class="info-row-compact">
                                <span class="info-label-compact">요금제</span>
                                <span class="info-value-compact">{{
                                    detailModalData.result.calcDetails?.costName || "-"
                                }}</span>
                            </div>
                            <div class="info-row-compact">
                                <span class="info-label-compact">사용량</span>
                                <span class="info-value-compact">
                                    {{ formatNumber(detailModalData.result.rawUsage, 0) }}
                                    {{ detailModalData.result.rawUnit || "-" }}
                                </span>
                            </div>
                            <div class="info-row-compact">
                                <span class="info-label-compact">청구 사용량</span>
                                <span class="info-value-compact">
                                    {{ formatNumber(detailModalData.result.billingUsage, 0) }}
                                    {{ detailModalData.result.billingUnit || "-" }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- 요금 요약 테이블 -->
                    <div class="fee-summary-table-compact">
                        <table class="fee-table-compact">
                            <tbody>
                                <tr>
                                    <td class="fee-label-compact">기본 요금</td>
                                    <td class="fee-value-compact">
                                        {{ formatNumber(detailModalData.result.baseFee, 0) }}원
                                    </td>
                                </tr>
                                <tr>
                                    <td class="fee-label-compact">사용량 요금</td>
                                    <td class="fee-value-compact">
                                        {{ formatNumber(detailModalData.result.usageFee, 0) }}원
                                    </td>
                                </tr>
                                <tr>
                                    <td class="fee-label-compact">기타 요금</td>
                                    <td class="fee-value-compact">
                                        {{ formatNumber(detailModalData.result.etcFee, 0) }}원
                                    </td>
                                </tr>
                                <tr>
                                    <td class="fee-label-compact">부가세</td>
                                    <td class="fee-value-compact">
                                        {{ formatNumber(detailModalData.result.vatAmount, 0) }}원
                                    </td>
                                </tr>
                                <tr>
                                    <td class="fee-label-compact">할인</td>
                                    <td class="fee-value-compact discount-compact">
                                        {{
                                            parseFloat(detailModalData.result.discountAmount) > 0
                                                ? `-${formatNumber(detailModalData.result.discountAmount, 0)}원`
                                                : "0원"
                                        }}
                                    </td>
                                </tr>
                                <tr class="fee-total-row-compact">
                                    <td class="fee-label-compact">총 요금</td>
                                    <td class="fee-value-compact total-compact">
                                        {{ formatNumber(detailModalData.result.totalFee, 0) }}원
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- 기타요금 상세 -->
                    <div
                        v-if="detailModalData.result.etcFeeDetails && detailModalData.result.etcFeeDetails.length > 0"
                        class="calc-section-compact"
                    >
                        <h6 class="section-subtitle-compact">기타요금 상세</h6>
                        <div class="calc-card-compact">
                            <div
                                class="etc-fees-list-compact"
                                :style="{
                                    gridTemplateColumns:
                                        detailModalData.result.etcFeeDetails.length === 1
                                            ? '1fr'
                                            : detailModalData.result.etcFeeDetails.length === 2
                                              ? 'repeat(2, 1fr)'
                                              : 'repeat(3, 1fr)',
                                }"
                            >
                                <div
                                    v-for="(detail, idx) in detailModalData.result.etcFeeDetails"
                                    :key="detail.detailIdx || idx"
                                    class="etc-fee-item-compact"
                                >
                                    <div class="etc-fee-header-compact">
                                        <div class="etc-fee-name-group-compact">
                                            <span class="etc-fee-name-compact">{{ detail.etcCostName }}</span>
                                            <span
                                                class="etc-fee-type-badge-compact"
                                                :class="detail.calcType?.toLowerCase()"
                                            >
                                                {{
                                                    detail.calcType === "USAGE"
                                                        ? "사용량"
                                                        : detail.calcType === "FIXED"
                                                          ? "고정"
                                                          : detail.calcType === "TOTALCOST"
                                                            ? "비율"
                                                            : detail.calcType
                                                }}
                                            </span>
                                        </div>
                                        <span class="etc-fee-amount-compact"
                                            >{{ formatNumber(detail.amount, 0) }}원</span
                                        >
                                    </div>
                                    <div class="etc-fee-formula-compact">
                                        <template v-if="detail.calcType === 'USAGE'">
                                            {{ formatNumber(detail.unitPrice, 0) }}원 ×
                                            {{ formatNumber(detailModalData.result.billingUsage, 0) }}
                                            {{ detailModalData.result.billingUnit }}
                                        </template>
                                        <template v-else-if="detail.calcType === 'FIXED'">
                                            {{ formatNumber(detail.fixedAmount, 0) }}원 (고정금액)
                                        </template>
                                        <template v-else-if="detail.calcType === 'TOTALCOST'">
                                            {{ formatNumber(detail.rate, 2) }}% ×
                                            {{ formatNumber(detailModalData.result.subTotal, 0) }}원
                                        </template>
                                    </div>
                                    <div v-if="detail.applyStartDt" class="etc-fee-period-compact">
                                        적용기간: {{ formatDate(detail.applyStartDt) }}
                                        <span v-if="detail.applyEndDt"> ~ {{ formatDate(detail.applyEndDt) }}</span>
                                        <span v-else> ~ 현재</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios";
import backEndApi from "@src/api/backEndApi.js";

export default {
    name: "BillCalculation",
    directives: {
        clickOutside: {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
    data() {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        return {
            calculating: false,
            contracts: [],
            // 날짜 선택 (YYYY-MM 형식)
            dateFrom: `${year}-${String(month).padStart(2, "0")}`,
            dateTo: `${year}-${String(month).padStart(2, "0")}`,
            // From 날짜 선택기
            showFromDatePicker: false,
            fromDateYear: year,
            fromDateMonth: month,
            // To 날짜 선택기
            showToDatePicker: false,
            toDateYear: year,
            toDateMonth: month,
            // 선택된 계약들
            selectedContracts: [],
            // 현재 선택된 계약 (결과 표시용)
            selectedContractIdx: null,
            // 계산 결과: { contractIdx: { regYm: { success, result } } }
            calculationResults: {},
            // 계약별 상태: { contractIdx: { success: count, failed: count, total: count } }
            contractStatus: {},
            // 프로그래스
            progress: {
                total: 0,
                completed: 0,
                current: null,
            },
            // 상세 모달 데이터
            detailModalData: null,
            // 계산 취소 관련
            isCalculationCancelled: false,
            cancelTokenSource: null,
        };
    },
    computed: {
        isAllContractsSelected() {
            return this.filteredContracts.length > 0 && this.selectedContracts.length === this.filteredContracts.length;
        },
        progressPercentage() {
            if (this.progress.total === 0) return 0;
            return Math.round((this.progress.completed / this.progress.total) * 100);
        },
        fromDateDisplay() {
            if (this.dateFrom) {
                const [year, month] = this.dateFrom.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return `${this.fromDateYear}년 ${this.fromDateMonth}월`;
        },
        toDateDisplay() {
            if (this.dateTo) {
                const [year, month] = this.dateTo.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return `${this.toDateYear}년 ${this.toDateMonth}월`;
        },
        // 계산 기간에 포함되는 계약만 필터링
        filteredContracts() {
            if (!this.dateFrom || !this.dateTo) {
                return this.contracts;
            }

            // 계산 기간의 시작일과 종료일 계산 (YYYY-MM 형식)
            const [fromYear, fromMonth] = this.dateFrom.split("-").map(Number);
            const [toYear, toMonth] = this.dateTo.split("-").map(Number);

            // 계산 기간의 첫날과 마지막날
            const calcStartDate = new Date(fromYear, fromMonth - 1, 1);
            const calcEndDate = new Date(toYear, toMonth, 0); // 해당 월의 마지막 날

            return this.contracts.filter((contract) => {
                const contractStartDate = new Date(contract.startDate);
                const contractEndDate = contract.endDate ? new Date(contract.endDate) : null;

                // 계약이 계산 기간과 겹치는지 확인
                // 계약 시작일 <= 계산 종료일 && (계약 종료일이 없거나 계약 종료일 >= 계산 시작일)
                const isOverlap =
                    contractStartDate <= calcEndDate && (contractEndDate === null || contractEndDate >= calcStartDate);

                return isOverlap;
            });
        },
    },
    mounted() {
        this.loadContracts();
    },
    // 설정 모달 keep-alive: 다른 탭에서 바뀐 목록만 다시 받고 입력·결과는 유지
    activated() {
        if (!this.wasDeactivated) return;
        this.loadContracts();
    },
    deactivated() {
        this.wasDeactivated = true;
    },
    methods: {
        async loadContracts() {
            try {
                const result = await backEndApi.energyBillCalc.getContractList();
                if (result.status === 200) {
                    this.contracts = result.data.filter((c) => c.enabled === "Y");
                } else {
                    await this.alertWarning(result.data.message || "계약 목록을 불러오는데 실패했습니다.");
                }
            } catch (error) {
                console.error("계약 목록 조회 실패:", error);
                await this.alertWarning("계약 목록을 불러오는데 실패했습니다.");
            }
        },

        // From 날짜 선택기 메서드
        toggleFromDatePicker() {
            this.showFromDatePicker = !this.showFromDatePicker;
            if (this.showFromDatePicker && this.dateFrom) {
                const [year, month] = this.dateFrom.split("-");
                this.fromDateYear = parseInt(year);
                this.fromDateMonth = parseInt(month);
            }
        },
        closeFromDatePicker() {
            this.showFromDatePicker = false;
        },
        changeFromYear(direction) {
            this.fromDateYear += direction;
            this.updateFromDate();
        },
        selectFromMonth(month) {
            this.fromDateMonth = month;
            this.showFromDatePicker = false;
            this.updateFromDate();
        },
        updateFromDate() {
            const year = String(this.fromDateYear);
            const month = String(this.fromDateMonth).padStart(2, "0");
            this.dateFrom = `${year}-${month}`;
        },

        // To 날짜 선택기 메서드
        toggleToDatePicker() {
            this.showToDatePicker = !this.showToDatePicker;
            if (this.showToDatePicker && this.dateTo) {
                const [year, month] = this.dateTo.split("-");
                this.toDateYear = parseInt(year);
                this.toDateMonth = parseInt(month);
            }
        },
        closeToDatePicker() {
            this.showToDatePicker = false;
        },
        changeToYear(direction) {
            this.toDateYear += direction;
            this.updateToDate();
        },
        selectToMonth(month) {
            this.toDateMonth = month;
            this.showToDatePicker = false;
            this.updateToDate();
        },
        updateToDate() {
            const year = String(this.toDateYear);
            const month = String(this.toDateMonth).padStart(2, "0");
            this.dateTo = `${year}-${month}`;
        },

        // 날짜 범위를 월별로 분할 (YYYY-MM 형식)
        splitDateRangeToMonths(dateFrom, dateTo) {
            const months = [];
            const [fromYear, fromMonth] = dateFrom.split("-").map(Number);
            const [toYear, toMonth] = dateTo.split("-").map(Number);

            let currentYear = fromYear;
            let currentMonth = fromMonth;

            while (currentYear < toYear || (currentYear === toYear && currentMonth <= toMonth)) {
                const regYm = `${currentYear}${String(currentMonth).padStart(2, "0")}`;
                months.push({ year: currentYear, month: currentMonth, regYm });

                // 다음 달로 이동
                currentMonth++;
                if (currentMonth > 12) {
                    currentMonth = 1;
                    currentYear++;
                }
            }

            return months;
        },

        // 계산 실행
        async executeCalculation() {
            if (!this.dateFrom || !this.dateTo) {
                await this.alertWarning("From과 To 날짜를 선택해주세요.");
                return;
            }

            if (this.selectedContracts.length === 0) {
                await this.alertWarning("계산할 계약을 선택해주세요.");
                return;
            }

            // 날짜 범위 검증 (YYYY-MM 형식)
            const [fromYear, fromMonth] = this.dateFrom.split("-").map(Number);
            const [toYear, toMonth] = this.dateTo.split("-").map(Number);
            const fromDate = new Date(fromYear, fromMonth - 1);
            const toDate = new Date(toYear, toMonth - 1);

            if (fromDate > toDate) {
                await this.alertWarning("From 날짜가 To 날짜보다 늦을 수 없습니다.");
                return;
            }

            this.calculating = true;
            this.calculationResults = {};
            this.contractStatus = {};
            this.isCalculationCancelled = false;

            // 날짜 범위를 월별로 분할
            const months = this.splitDateRangeToMonths(this.dateFrom, this.dateTo);

            // 작업 리스트 생성
            const tasks = [];
            this.selectedContracts.forEach((contractIdx) => {
                months.forEach((month) => {
                    tasks.push({ contractIdx, regYm: month.regYm });
                });
            });

            // 프로그래스 초기화
            this.progress = {
                total: tasks.length,
                completed: 0,
                current: null,
            };

            // 프로그래스 팝업 표시
            this.$refs.progressModal.show();

            try {
                // 월 단위 배치 처리 (서버 병렬 + 프로그레스 유지)
                for (const month of months) {
                    if (this.isCalculationCancelled) {
                        console.log("계산이 취소되었습니다.");
                        break;
                    }

                    const contractCount = this.selectedContracts.length;
                    this.progress.current = `${this.formatYearMonth(month.regYm)} - ${contractCount}개 계약 계산 중...`;

                    try {
                        const result = await backEndApi.energyBillCalc.calculateBillBatch({
                            regYm: month.regYm,
                            contractIdxList: [...this.selectedContracts],
                            maxConcurrency: 5,
                            abnormalCheck: 1,
                        });

                        if (this.isCalculationCancelled) {
                            break;
                        }

                        const batchData = result.status === 200 ? result.data : null;
                        const successItems = batchData?.results || [];
                        const errorItems = batchData?.errors || [];

                        const processedContractIdxs = new Set();

                        for (const item of successItems) {
                            processedContractIdxs.add(item.contractIdx);
                            if (!this.calculationResults[item.contractIdx]) {
                                this.calculationResults[item.contractIdx] = {};
                            }
                            this.calculationResults[item.contractIdx][month.regYm] = {
                                success: true,
                                result: item.result,
                            };
                            this.progress.completed++;
                        }

                        for (const item of errorItems) {
                            processedContractIdxs.add(item.contractIdx);
                            if (!this.calculationResults[item.contractIdx]) {
                                this.calculationResults[item.contractIdx] = {};
                            }
                            this.calculationResults[item.contractIdx][month.regYm] = {
                                success: false,
                                error: item.error || item.message || "계산 실패",
                            };
                            this.progress.completed++;
                        }

                        if (batchData) {
                            for (const contractIdx of this.selectedContracts) {
                                if (processedContractIdxs.has(contractIdx)) continue;
                                if (!this.calculationResults[contractIdx]) {
                                    this.calculationResults[contractIdx] = {};
                                }
                                this.calculationResults[contractIdx][month.regYm] = {
                                    success: false,
                                    error: "해당 월에 유효하지 않은 계약입니다.",
                                };
                                this.progress.completed++;
                            }
                        }

                        // 배치 자체 실패 또는 누락 계약 처리
                        if (!batchData) {
                            for (const contractIdx of this.selectedContracts) {
                                if (processedContractIdxs.has(contractIdx)) continue;
                                if (!this.calculationResults[contractIdx]) {
                                    this.calculationResults[contractIdx] = {};
                                }
                                this.calculationResults[contractIdx][month.regYm] = {
                                    success: false,
                                    error: result.data?.message || result.data?.detail || "계산 실패",
                                };
                                this.progress.completed++;
                            }
                        }
                    } catch (error) {
                        console.error(`배치 계산 실패 (월: ${month.regYm}):`, error);
                        for (const contractIdx of this.selectedContracts) {
                            if (!this.calculationResults[contractIdx]) {
                                this.calculationResults[contractIdx] = {};
                            }
                            this.calculationResults[contractIdx][month.regYm] = {
                                success: false,
                                error: error.message || error.detail || "계산 실패",
                            };
                            this.progress.completed++;
                        }
                    }
                }

                // 취소되지 않은 경우에만 결과 처리
                if (!this.isCalculationCancelled) {
                    // 계약별 상태 계산
                    this.selectedContracts.forEach((contractIdx) => {
                        const results = this.calculationResults[contractIdx] || {};
                        let success = 0;
                        let failed = 0;

                        Object.values(results).forEach((result) => {
                            if (result.success) {
                                success++;
                            } else {
                                failed++;
                            }
                        });

                        this.$set(this.contractStatus, contractIdx, {
                            success,
                            failed,
                            total: success + failed,
                        });
                    });

                    // 첫 번째 계약 결과 표시
                    if (this.selectedContracts.length > 0) {
                        this.selectedContractIdx = this.selectedContracts[0];
                    }

                    // 계산 완료 상태로 업데이트 (프로그레스 모달에서 완료 UI 표시)
                    this.progress.current = "계산 완료";
                }
            } catch (error) {
                // 취소로 인한 에러는 무시
                if (axios.isCancel(error)) {
                    console.log("요청이 취소되었습니다:", error.message);
                } else if (this.isCalculationCancelled) {
                    console.log("계산이 취소되었습니다.");
                } else {
                    console.error("계산 실행 실패:", error);
                    await this.alertWarning("계산 실행 중 오류가 발생했습니다.");
                }
            } finally {
                this.calculating = false;

                // 취소된 경우에만 모달 닫기
                if (this.isCalculationCancelled) {
                    this.$refs.progressModal.hide();
                    this.calculationResults = {};
                    this.contractStatus = {};
                    this.selectedContractIdx = null;
                    await this.alertWarning("계산이 취소되었습니다.");
                }
                // 취소되지 않은 경우, 프로그레스 모달은 완료 상태로 유지 (사용자가 확인 버튼으로 닫음)

                // 상태 초기화
                this.isCalculationCancelled = false;
                this.cancelTokenSource = null;
            }
        },

        // 프로그레스 모달 닫기
        closeProgressModal() {
            this.$refs.progressModal.hide();
        },

        // 계산 취소
        async cancelCalculation() {
            const result = await this.$swal({
                type: "question",
                title: "계산 취소",
                html: "계산을 취소하시겠습니까?<br />진행 중인 계산은 중단되고 결과가 초기화됩니다.",
                showCancelButton: true,
                confirmButtonText: "취소",
                cancelButtonText: "계속",
                confirmButtonColor: "#ef4444",
                cancelButtonColor: "#6b7280",
            });

            if (!result.value) {
                return;
            }

            this.isCalculationCancelled = true;

            // CancelToken이 있으면 취소
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel("사용자가 계산을 취소했습니다.");
            }
        },

        // 계약 선택
        selectContract(contractIdx) {
            if (this.calculationResults[contractIdx]) {
                this.selectedContractIdx = contractIdx;
            }
        },

        // 전체 계약 선택/해제
        toggleSelectAllContracts(event) {
            if (event.target.checked) {
                this.selectedContracts = this.filteredContracts.map((c) => c.contractIdx);
            } else {
                this.selectedContracts = [];
            }
        },

        // 선택된 계약의 월별 결과 가져오기
        getSelectedContractResults() {
            if (!this.selectedContractIdx || !this.calculationResults[this.selectedContractIdx]) {
                return {};
            }
            return this.calculationResults[this.selectedContractIdx];
        },

        // 상세 모달 열기
        openDetailModal(contractIdx, regYm) {
            const results = this.calculationResults[contractIdx];
            if (results && results[regYm] && results[regYm].success) {
                this.detailModalData = {
                    contractIdx,
                    regYm,
                    result: results[regYm].result,
                };
                this.$refs.detailModal.show();
            }
        },

        getContractName(contractIdx) {
            const contract = this.contracts.find((c) => c.contractIdx === contractIdx);
            return contract?.contractName || contract?.point?.ptName || `계약-${contractIdx}`;
        },

        formatYearMonth(ym) {
            if (!ym) return "-";
            if (ym.length === 6) {
                return `${ym.substring(0, 4)}년 ${ym.substring(4, 6)}월`;
            }
            return ym;
        },

        formatDate(dateStr) {
            if (!dateStr) return "-";
            if (dateStr.includes("-")) return dateStr;
            if (dateStr.length === 8) {
                return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            }
            return dateStr;
        },

        formatNumber(value, decimals = 2) {
            return new Intl.NumberFormat("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(value || 0);
        },
        // 청구 기간 포맷팅 (YYYYMMDD → YYYY-MM-DD)
        formatBillingPeriod(startAt, endAt) {
            if (!startAt || !endAt) return "-";
            const formatDate = (dateStr) => {
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
                }
                return dateStr;
            };
            return `${formatDate(startAt)} ~ ${formatDate(endAt)}`;
        },
        // 계산 방식 이름 변환
        getCalcTypeName(calcType) {
            const typeMap = {
                USAGE: "요율",
                FIXED: "금액",
            };
            return typeMap[calcType] || calcType;
        },
    },
};
</script>

<style scoped>
.bill-calculation {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* 헤더 */
.content-header {
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
    display: flex;
    align-items: center;
    gap: 16px;
}

.content-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.content-header h3 i {
    color: var(--ebc-primary);
}

/* 헤더 안의 모드 선택 */
.mode-selector-header {
    display: flex;
    gap: 8px;
    align-items: center;
}

.mode-btn-header {
    padding: 6px 16px;
    border: 2px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}

.mode-btn-header:hover {
    border-color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
}

.mode-btn-header.active {
    border-color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
    color: var(--ebc-primary);
}

.mode-btn-header i {
    font-size: 14px;
}

/* 계산 섹션 */
.calculation-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.calc-form {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    box-shadow: var(--ebc-shadow-panel);
}

.calc-form-compact {
    display: flex;
    align-items: center;
    gap: 12px;
}

.calc-form-compact .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.calc-form-compact .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.form-select-compact {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 150px;
    height: 32px;
}

.form-select-compact:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.date-picker-container-compact {
    position: relative;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 0;
    z-index: 100;
    min-width: 120px;
    height: 32px;
}

.date-display-compact {
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 120px;
    height: 100%;
    box-sizing: border-box;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 12px;
    background: transparent;
}

.date-display-compact:hover {
    background: transparent;
}

.date-display-compact i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display-compact i.rotate {
    transform: rotate(180deg);
}

.btn-calculate-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-calculate-compact:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-calculate-compact:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

.section-title {
    margin: 0 0 20px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

/* 인라인 폼 그룹 */
.form-group-inline {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    align-items: flex-start;
}

.form-field {
    display: flex;
    flex-direction: column;
}

.form-field label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.required {
    color: var(--ebc-danger);
}

.form-input,
.form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.help-text {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: var(--ebc-text-muted);
}

.button-group {
    margin-top: 24px;
}

.btn-calculate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-lg);
    padding: 0 20px;
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    width: 100%;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-calculate:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-calculate:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
}

.btn-calculate-inline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-calculate-inline:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-calculate-inline:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
}

/* 계약 선택 */
.contract-selection-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.contract-selection-header label {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
    margin: 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-select-all,
.btn-deselect-all {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-select-all:hover {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
    color: var(--ebc-positive);
}

.btn-deselect-all:hover {
    background: var(--ebc-danger-tint);
    border-color: var(--ebc-danger);
    color: var(--ebc-danger);
}

.selection-summary {
    padding: 4px 12px;
    background: var(--ebc-positive-tint);
    border: 1px solid var(--ebc-positive-tint);
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-positive);
}

.contract-selection-list {
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    background: var(--ebc-surface-sunken);
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
}

.contract-checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: var(--ebc-radius-sm);
    cursor: pointer;
    transition: all 0.2s;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    white-space: nowrap;
}

.contract-checkbox-label:hover {
    background: var(--ebc-primary-tint);
    border-color: var(--ebc-primary);
}

.contract-checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--ebc-primary);
    vertical-align: middle;
    flex-shrink: 0;
}

.contract-checkbox-label input[type="checkbox"]:checked + .contract-name {
    color: var(--ebc-primary);
    font-weight: 700;
}

.contract-checkbox-label .contract-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    margin-left: 5px;
}

/* 배치 계산용 컴팩트 스타일 */
.calc-form-compact-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.contract-selection-compact {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.contract-selection-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.contract-selection-header-compact label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    margin: 0;
    white-space: nowrap;
}

.header-actions-compact {
    display: flex;
    align-items: center;
    gap: 6px;
}

.btn-select-all-compact,
.btn-deselect-all-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-select-all-compact:hover {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
    color: var(--ebc-positive);
}

.btn-deselect-all-compact:hover {
    background: var(--ebc-danger-tint);
    border-color: var(--ebc-danger);
    color: var(--ebc-danger);
}

.btn-select-all-compact i,
.btn-deselect-all-compact i {
    font-size: 10px;
}

.selection-summary-compact {
    padding: 4px 8px;
    background: var(--ebc-positive-tint);
    border: 1px solid var(--ebc-positive-tint);
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-positive);
    white-space: nowrap;
}

.contract-selection-list-compact {
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 8px;
    background: var(--ebc-surface-sunken);
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-height: 150px;
    overflow-y: auto;
}

.contract-checkbox-label-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: var(--ebc-radius-sm);
    cursor: pointer;
    transition: all 0.2s;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    white-space: nowrap;
    margin-bottom: 0px !important;
}

.contract-checkbox-label-compact:hover {
    background: var(--ebc-primary-tint);
    border-color: var(--ebc-primary);
}

.contract-checkbox-label-compact input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: var(--ebc-primary);
    vertical-align: middle;
    flex-shrink: 0;
}

.contract-checkbox-label-compact input[type="checkbox"]:checked + .contract-name-compact {
    color: var(--ebc-primary);
    font-weight: 700;
}

.contract-checkbox-label-compact .contract-name-compact {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text);
    margin-left: 2px;
}

.form-input-compact {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 80px;
    height: 32px;
    box-sizing: border-box;
}

.form-input-compact:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

/* 계약 선택 드롭다운 */
.contract-dropdown-container-compact {
    position: relative;
    cursor: pointer;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    z-index: 100;
    min-width: 180px;
    height: 32px;
}

.contract-dropdown-display-compact {
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 180px;
    height: 100%;
    box-sizing: border-box;
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 12px;
}

.contract-dropdown-display-compact:hover {
    background: var(--ebc-track);
}

.contract-dropdown-display-compact i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.contract-dropdown-display-compact i.rotate {
    transform: rotate(180deg);
}

.contract-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 101;
    margin-top: 6px;
    overflow: hidden;
    min-width: 300px;
    max-width: 500px;
}

.contract-dropdown-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: var(--ebc-surface-sunken);
    border-bottom: 1px solid var(--ebc-border);
}

.btn-select-all-dropdown,
.btn-deselect-all-dropdown {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-select-all-dropdown:hover {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
    color: var(--ebc-positive);
}

.btn-deselect-all-dropdown:hover {
    background: var(--ebc-danger-tint);
    border-color: var(--ebc-danger);
    color: var(--ebc-danger);
}

.btn-select-all-dropdown i,
.btn-deselect-all-dropdown i {
    font-size: 10px;
}

.selection-count-dropdown {
    margin-left: auto;
    padding: 4px 8px;
    background: var(--ebc-positive-tint);
    border: 1px solid var(--ebc-positive-tint);
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-positive);
    white-space: nowrap;
}

.contract-dropdown-list {
    max-height: 250px;
    overflow-y: auto;
    padding: 8px;
}

.contract-checkbox-dropdown {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: var(--ebc-radius-xs);
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 2px;
}

.contract-checkbox-dropdown:hover {
    background: var(--ebc-primary-tint);
}

.contract-checkbox-dropdown input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--ebc-primary);
    flex-shrink: 0;
}

.contract-checkbox-dropdown input[type="checkbox"]:checked + .contract-name-dropdown {
    color: var(--ebc-primary);
    font-weight: 700;
}

.contract-name-dropdown {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    flex: 1;
}

/* 결과 섹션 */
.result-section {
    background: white;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    padding: 24px;
}

.result-card {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    padding: 20px;
}

.result-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.result-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.result-item.highlight {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
}

.result-item.discount {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
}

.result-item.total {
    grid-column: 1 / -1;
    background: var(--ebc-primary);
    color: white;
    font-size: 16px;
    font-weight: 700;
    padding: 16px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
}

.result-item.total .result-label,
.result-item.total .result-value {
    color: white;
}

.result-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.result-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-text);
}

/* 배치 계산 결과 */
.batch-summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: var(--ebc-radius-xs);
    border: 2px solid;
}

.summary-item.success {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
    color: var(--ebc-positive);
}

.summary-item.failed {
    background: var(--ebc-danger-tint);
    border-color: var(--ebc-danger);
    color: var(--ebc-danger);
}

.summary-item.total {
    background: var(--ebc-primary-tint);
    border-color: var(--ebc-primary);
    color: var(--ebc-primary-hover);
}

.summary-item i {
    font-size: 15px;
    flex-shrink: 0;
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.summary-label {
    font-size: 12px;
    font-weight: 600;
}

.summary-value {
    font-size: 16px;
    font-weight: 700;
    margin-left: auto;
}

/* 진행 상황 리스트 */
.progress-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.progress-item {
    padding: 16px;
    border-radius: var(--ebc-radius-sm);
    border: 2px solid;
    background: white;
}

.progress-item.success {
    border-color: var(--ebc-positive);
    background: var(--ebc-positive-tint);
}

.progress-item.failed {
    border-color: var(--ebc-danger);
    background: var(--ebc-danger-tint);
}

.progress-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    cursor: pointer;
}

.progress-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.progress-right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.status-icon {
    font-size: 15px;
    flex-shrink: 0;
}

.progress-item.success .status-icon {
    color: var(--ebc-positive);
}

.progress-item.failed .status-icon {
    color: var(--ebc-danger);
}

.contract-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.total-fee {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    white-space: nowrap;
}

.btn-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--ebc-text-sub);
}

.btn-toggle:hover {
    background: var(--ebc-surface-sunken);
    border-color: var(--ebc-primary);
    color: var(--ebc-primary);
}

.btn-toggle i {
    font-size: 10px;
}

.error-message {
    padding: 8px 12px;
    background: var(--ebc-danger-tint);
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    color: var(--ebc-danger);
    display: flex;
    align-items: center;
    gap: 8px;
}

/* 간단한 요약 (항상 표시) */
.result-summary {
    display: flex;
    gap: 16px;
    padding: 8px 0 0 0;
    font-size: 11px;
    color: var(--ebc-text-sub);
}

.result-summary span {
    white-space: nowrap;
}

/* 상세 내역 (펼쳤을 때) */
.result-detail-expanded {
    margin-top: 12px;
}

.detail-separator {
    height: 1px;
    background: var(--ebc-border);
    margin: 12px 0;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background: white;
    border-radius: var(--ebc-radius-xs);
    border: 1px solid var(--ebc-border);
}

.detail-row.highlight {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
}

.detail-row.discount {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
}

.detail-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.detail-value {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
}

/* 오류 섹션 */
.error-section {
    margin-top: 24px;
    padding: 16px;
    background: var(--ebc-danger-tint);
    border: 1px solid var(--ebc-danger-tint);
    border-radius: var(--ebc-radius-sm);
}

.error-section h5 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-danger);
}

.error-item {
    padding: 8px 12px;
    background: white;
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    color: var(--ebc-danger);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.error-item:last-child {
    margin-bottom: 0;
}

/* 결과 정보 섹션 */
.result-info-section {
    padding: 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    margin-bottom: 20px;
    border: 1px solid var(--ebc-border);
}

.info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    min-width: 80px;
}

.info-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

/* 기타요금 상세 */
.etc-fee-details {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 2px solid var(--ebc-border);
}

.etc-fee-title {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.etc-fee-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
}

.etc-fee-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s;
}

.etc-fee-item:hover {
    border-color: var(--ebc-primary);
}

.etc-fee-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.etc-fee-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
    flex: 1;
}

.etc-fee-amount {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-primary);
    white-space: nowrap;
}

.etc-fee-calc {
    font-size: 12px;
    color: var(--ebc-text-sub);
    padding: 6px 10px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    word-break: keep-all;
    line-height: 1.5;
}

/* 시간대별 사용량 */
.time-slot-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 2px solid var(--ebc-border);
}

.time-slot-title {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.time-slot-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.time-slot-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.time-slot-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.time-slot-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-text);
}

.time-slot-fee {
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-primary);
}

.detail-info-section {
    padding: 12px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 12px;
}

/* 날짜 선택기 스타일 (년월만 선택) */
.date-picker-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    padding: 0;
    z-index: 100;
    height: 100%;
    min-height: 38px;
}

.date-display {
    padding: 10px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    width: 100%;
    justify-content: center;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 13px;
    height: 100%;
    box-sizing: border-box;
}

.date-display:hover {
    background: var(--ebc-track);
}

.date-display i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display i.rotate {
    transform: rotate(180deg);
}

.date-dropdown {
    position: absolute;
    top: 100%;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 101;
    margin-top: 6px;
    padding: 16px;
    min-width: 240px;
}

.date-dropdown-left {
    left: 0;
}

.date-dropdown-right {
    right: 0;
}

.year-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-border);
}

.year-selector button {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 4px 8px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s ease;
    font-weight: 600;
    font-size: 12px;
}

.year-selector button:focus {
    outline: none;
}

.year-selector button:hover {
    background: var(--ebc-primary);
    color: white;
    border-color: var(--ebc-primary);
}

.year-selector span {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.month-btn {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 8px 4px;
    cursor: pointer;
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s ease;
    text-align: center;
}

.month-btn:hover {
    background: var(--ebc-border);
    border-color: var(--ebc-border-strong);
    color: var(--ebc-text);
}

.month-btn.active {
    background: var(--ebc-primary);
    border-color: var(--ebc-primary);
    color: white;
    box-shadow: 0 1px 4px var(--ebc-primary-tint-strong);
}

/* 헤더 폼 */
.calc-form-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    justify-content: flex-end;
}

.calc-form-header .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.calc-form-header .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.form-input-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    height: 32px;
}

.form-input-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.btn-calculate-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-calculate-header:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-calculate-header:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

/* 2컬럼 레이아웃 */
.calculation-layout {
    display: grid;
    grid-template-columns: 6fr 5fr;
    gap: 20px;
    align-items: start;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

/* 왼쪽 패널 */
.left-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 0;
    align-self: start;
    height: 100%;
    overflow-y: auto;
}

/* 날짜 선택 섹션 */
.date-selection-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    /* margin-bottom: 12px; */
    box-shadow: var(--ebc-shadow-panel);
}

.date-form-compact {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
}

.date-form-compact .form-field-inline {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
}

.date-form-compact .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    margin: 0;
    white-space: nowrap;
}

.contract-list-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    height: 100%;
    box-shadow: var(--ebc-shadow-panel);
}

.contract-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.contract-table thead th {
    background: var(--ebc-surface-sunken);
    border-bottom: 1px solid var(--ebc-hairline);
    padding: 10px 8px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    text-align: left;
    font-size: 11px;
}

.contract-table tbody td {
    padding: 10px 8px;
    border-bottom: 1px solid var(--ebc-track);
}

.contract-table tbody tr {
    cursor: pointer;
    transition: background 0.2s;
}

.contract-table tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

.contract-table tbody tr.selected {
    background: var(--ebc-primary-tint);
    border-left: 3px solid var(--ebc-primary);
}

.contract-table tbody tr.selected td {
    font-weight: 600;
}

.status-badge {
    padding: 4px 8px;
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
    font-weight: 600;
}

.status-badge.success {
    background: var(--ebc-positive-tint);
    color: var(--ebc-positive);
}

.status-badge.has-error {
    background: var(--ebc-danger-tint);
    color: var(--ebc-danger);
}

/* 오른쪽 패널 */
.right-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

.result-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    /* padding-right: 8px; */
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    box-shadow: var(--ebc-shadow-panel);
}

.month-results {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.month-result-card {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    transition: all 0.2s;
}

.month-result-card:hover {
    border-color: var(--ebc-primary);
}

.month-result-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.month-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
    flex-shrink: 0;
}

.month-summary {
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-primary);
    flex: 1;
    text-align: right;
}

.btn-detail-modal {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    color: var(--ebc-text-sub);
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-detail-modal:hover {
    background: var(--ebc-primary);
    border-color: var(--ebc-primary);
    color: white;
}

.section-title-compact {
    margin: 0 0 10px 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    padding-bottom: 6px;
    border-bottom: 1px solid var(--ebc-border);
}

/* Placeholder */
.placeholder-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 40px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    box-shadow: var(--ebc-shadow-panel);
}

.placeholder-content {
    text-align: center;
    color: var(--ebc-text-muted);
}

.placeholder-content i {
    font-size: 28px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.placeholder-content p {
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
}

/* 프로그래스 팝업 */
.progress-modal-content {
    padding: 20px;
}

.progress-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-text);
}

.progress-info i {
    color: var(--ebc-primary);
    font-size: 15px;
}

.progress-complete-icon {
    color: var(--ebc-positive) !important;
}

.progress-bar-wrapper {
    width: 100%;
    height: 24px;
    background: var(--ebc-border);
    border-radius: var(--ebc-radius-md);
    overflow: hidden;
    margin-bottom: 12px;
}

.progress-bar {
    height: 100%;
    background: var(--ebc-primary);
    transition: width 0.3s ease;
    border-radius: var(--ebc-radius-md);
}

.progress-text {
    text-align: center;
    font-size: 12px;
    color: var(--ebc-text-sub);
    font-weight: 600;
}

.progress-actions {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--ebc-border);
}

.btn-cancel-calculation {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: var(--ebc-danger);
    color: white;
    border: none;
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-cancel-calculation:hover {
    background: var(--ebc-danger);
}

.btn-cancel-calculation:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.btn-cancel-calculation i {
    font-size: 14px;
}

.btn-close-progress {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: var(--ebc-positive);
    color: white;
    border: none;
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-close-progress:hover {
    background: var(--ebc-positive);
}

.btn-close-progress:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.btn-close-progress i {
    font-size: 14px;
}

/* 상세 모달 */
.detail-modal-content {
    padding: 0;
}

.detail-modal-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;
}

/* BillVerification 스타일 참고 */
.info-section-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    margin-bottom: 12px;
}

.info-grid-compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.info-row-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
}

.info-label-compact {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value-compact {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text);
    text-align: right;
}

.fee-summary-table-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    overflow: hidden;
}

.fee-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.fee-table-compact tbody tr {
    border-bottom: 1px solid var(--ebc-track);
}

.fee-table-compact tbody tr:last-child {
    border-bottom: none;
}

.fee-label-compact {
    padding: 8px 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    font-size: 11px;
}

.fee-value-compact {
    padding: 8px 10px;
    text-align: right;
    font-weight: 700;
    color: var(--ebc-text);
    font-size: 12px;
}

.fee-value-compact.discount-compact {
    color: var(--ebc-danger);
}

.fee-total-row-compact {
    background: var(--ebc-primary);
}

.fee-total-row-compact .fee-label-compact,
.fee-total-row-compact .fee-value-compact {
    color: white;
    font-size: 12px;
}

.fee-total-row-compact .fee-value-compact.total-compact {
    font-size: 14px;
    font-weight: 700;
}

.calc-section-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px 12px;
    margin-bottom: 10px;
}

.section-subtitle-compact {
    margin: 0 0 8px 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    padding: 0;
    border: none;
}

.calc-card-compact {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px;
    margin-top: 8px;
}

.etc-fees-list-compact {
    display: grid;
    gap: 8px;
    margin: 8px 0;
}

.etc-fee-item-compact {
    padding: 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.etc-fee-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.etc-fee-name-group-compact {
    display: flex;
    align-items: center;
    gap: 6px;
}

.etc-fee-name-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text);
}

.etc-fee-type-badge-compact {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 4px;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-border);
    color: var(--ebc-text-sub);
}

.etc-fee-type-badge-compact.usage,
.etc-fee-type-badge-compact.fixed {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

.etc-fee-amount-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-primary);
}

.etc-fee-formula-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    padding: 4px 6px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 4px;
}

.etc-fee-period-compact {
    font-size: 10px;
    color: var(--ebc-text-muted);
    margin-top: 4px;
}

/* 계약 기간 표시 */
.period-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 11px;
    color: var(--ebc-text-sub);
}

.ongoing-badge {
    display: inline-block;
    padding: 2px 6px;
    background: var(--ebc-positive-tint);
    color: var(--ebc-positive);
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
    font-weight: 600;
    margin-left: 4px;
}

/* 반응형 */
@media (max-width: 1024px) {
    .calculation-layout {
        grid-template-columns: 1fr;
    }
}
</style>
